import { z } from 'zod';

/**
 * Commercial brand / business Hero operates.
 *
 * - `hero` — the platform itself: `tryhero.io`, `app.tryhero.io`, mobile apps,
 *   Hero WhatsApp agent.
 * - `wathiqa_plus` — وثيقة+, government waybills over WhatsApp.
 *
 * Persisted on `ProductPurchase.business`, `Order.business`, Lead funnels, and
 * derived from `doc.app` via `appBusiness()` in `@hero/core`. Registry:
 * `BUSINESS_REGISTRY` in `@hero/core`.
 *
 * Formerly named `Product` — that name now means the audience offering under
 * a business (see {@link productSchema}).
 */
export const businessSchema = z.enum(['hero', 'wathiqa_plus']);
export type Business = z.infer<typeof businessSchema>;

/**
 * Audience offering under a {@link businessSchema}.
 *
 * Replaces the old `SubscriptionAudience` (`org` | `freelancer`) as a catalog
 * node: enterprise ≈ company Org, freelancer ≈ owner-operator Driver.
 *
 * Seat vs order is **not** a product — it is {@link billingUnitSchema} on
 * {@link productPurchaseSchema}.
 */
export const productSchema = z.enum([
  'hero_enterprise',
  'hero_freelancer',
  'wathiqa_plus_enterprise',
  'wathiqa_plus_freelancer',
]);
export type Product = z.infer<typeof productSchema>;

/**
 * What a paid quantity is **counted** in — the meter behind a price.
 *
 * Display noun for prepaid credits (`order` vs `waybill`) lives on
 * `BUSINESS_REGISTRY[business].creditUnit` — not here.
 */
export const billingUnitSchema = z.enum(['order', 'vehicle']);
export type BillingUnit = z.infer<typeof billingUnitSchema>;

/**
 * Shared commercial identity for “what was sold / granted”.
 *
 * Embedded on Subscription, Billing, BillingItem, subscription snapshots,
 * Lead sale meta, and Moyasar metadata — not scattered top-level fields.
 *
 * Order and Lead funnel rows stamp only {@link businessSchema} (brand
 * provenance / one funnel per brand).
 */
export const productPurchaseSchema = z.object({
  business: businessSchema,
  product: productSchema,
  planCode: z.string(),
  unit: billingUnitSchema,
  /** Set when this purchase line is an addon (e.g. `whatsapp`). */
  addonCode: z.string().optional(),
});
export type ProductPurchase = z.infer<typeof productPurchaseSchema>;

/**
 * "Which product / surface" a client is. See `docs/ai/user-signals.md`.
 *
 * Named `HeroAppId` to keep it clear of `AppId` in
 * `model/app/app.model.ts`, which identifies a third-party app in the
 * integrations marketplace. This one is Hero's own front-ends.
 *
 * Sent by the SDK on every request as the `x-hero-app` header (paired
 * with `x-hero-platform`, which uses {@link heroPlatformSchema}), and
 * persisted in three places that all read from this one enum:
 * `User.meta.presence.apps[].app`, `User.meta.lastSeenOnline.app` /
 * `Org.meta.lastSeenOnline.app`, and `doc.app` on every document.
 *
 * ## Naming
 *
 * **A bare name means Hero.** `dashboard`, `carrier_app`, `landing` are
 * Hero's. Anything belonging to another business carries the business
 * prefix — `wathiqa_plus_agent` today, `wathiqa_plus_landing` and
 * `wathiqa_plus_dashboard` when those ship.
 *
 * ## The dashboard is one app
 *
 * The platform is converging on a single authenticated web app at
 * `app.tryhero.io` served by the `dashboard` CI job — see the "Single
 * web dashboard direction" rule in `.cursorrules`. Every network party
 * (customer, carrier, broker, driver, individual customer) reports as
 * `dashboard`; we don't split `carrier_dashboard` / `customer_dashboard`.
 * `admin_panel` stays separate — it's Hero back-office, not a
 * network-party surface.
 *
 * ## Agent surfaces get an app id
 *
 * On WhatsApp the agent *is* the client, so it gets its own id rather
 * than borrowing a front-end's. One number can serve a carrier and a
 * customer in the same hour and that is correct — audience is not part
 * of app identity (capability checks are permission-based per the
 * network model). Role on a given turn lives on the Conversation /
 * agent CLS persona, not on the document envelope.
 *
 * The judgement call worth knowing: **in-app AI chat inside the
 * dashboard stamps `dashboard`, not `hero_agent`** — `app` answers "what
 * did the human have in front of them". `doc.isAgent` marks AI
 * authorship either way.
 *
 * `landing` covers the unauthenticated marketing surface at `tryhero.io`
 * (Next.js `apps/landing-v2`). Landing traffic isn't authed, so
 * `PresenceInterceptor` never bumps a presence entry keyed as `landing`
 * — but the value is in the enum so the SDK type-checks and the
 * `x-hero-app` header still tags lead submissions with the correct
 * origin surface.
 */
export const heroAppIdSchema = z.enum([
  // Hero — bare names, because unprefixed means Hero.
  'carrier_app',
  'customer_app',
  'dashboard',
  'admin_panel',
  'landing',
  // Agent surfaces — the agent IS the client here, so it gets an app id.
  'hero_agent',
  'wathiqa_plus_agent',
  // Future white-label surfaces land as 'wathiqa_plus_landing',
  // 'wathiqa_plus_dashboard' — one entry each, nothing else changes.
]);
export type HeroAppId = z.infer<typeof heroAppIdSchema>;

/**
 * How the human reaches a surface. Derived from the app id via
 * `appSurface()` in `@hero/core`, never stored alongside it.
 *
 * `whatsapp` and `voice` are separate because the constraints differ:
 * WhatsApp has templates, buttons, and a 24-hour service window; a phone
 * call has none of those and cannot render a card.
 */
export const appSurfaceSchema = z.enum(['web', 'mobile', 'whatsapp', 'voice', 'admin']);
export type AppSurface = z.infer<typeof appSurfaceSchema>;

/**
 * Runtime an app instance is being reached through — not the device OS.
 *
 * Set from the `x-hero-platform` header on SDKs (`web` | `ios` |
 * `android`). Agent CLS / presence stamp `whatsapp` (or later `phone`)
 * instead of guessing iOS vs Android — Meta does not tell us the phone
 * OS, and a phone call is a different runtime from WhatsApp.
 *
 * `unknown` is only for a missing or unparseable header, never the
 * WhatsApp default.
 *
 * `RefreshSessionPlatform` is an alias of this — one vocabulary across
 * the auth, session, presence, and provenance layers. It lives here (a
 * `common` leaf) rather than in the refresh-session model so that
 * `doc.schema.ts` can reference it without the doc envelope importing a
 * model, which would close a barrel cycle.
 */
export const heroPlatformSchema = z.enum([
  'web',
  'ios',
  'android',
  'whatsapp',
  'phone',
  'unknown',
]);
export type HeroPlatform = z.infer<typeof heroPlatformSchema>;
